import React, { useState, useCallback, useEffect } from "react";
import _ from "underscore";
import styles from "./classConsolidatedData.module.css";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import DatabaseIcon from "../../../assets/icons/classes.svg";
import { useInstitution } from "../../../context/InstitutionContext";
import { classConsolidatedData } from "../../../services/education";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/loading.gif";
import api from "../../../http/api";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";

const ClassConsolidatedData = () => {
  const { institution } = useInstitution();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [consolidatedData, setConsolidatedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [paginationInfo, setPaginationInfo] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const fetchConsolidatedData = useCallback((pageNumber = 1, searchTerm = "") => {
    const debouncedFetch = _.debounce(async () => {
      try {
        setLoading(true);
        
        const response = await classConsolidatedData({
          institutionId: institution.id,
          page: pageNumber,
          pageSize: itemsPerPage,
          searchTerm: searchTerm
        });
        
        setLastApiResponse(response.classData);
        
        setConsolidatedData(response.classData.classConsolidatedData);
        setPaginationInfo({
          pageNumber: response.classData.currentPage,
          pageSize: itemsPerPage,
          totalCount: response.classData.totalResults,
          totalPages: response.classData.totalPages,
          hasPreviousPage: response.classData.currentPage > 1,
          hasNextPage: response.classData.currentPage < response.classData.totalPages
        });
      } catch (error) {
        console.error("Erro ao buscar dados consolidados de turma:", error);
        setConsolidatedData([]);
        setPaginationInfo(null);
      } finally {
        setLoading(false);
      }
    }, 1000);
    
    debouncedFetch();
  }, [institution?.id, itemsPerPage]);

  const processedConsolidatedData = consolidatedData ? consolidatedData.map((item) => ({
    classId: item.classId || '-',
    className: item.className || '-',
    classStatus: item.classStatus || '-',
    knowledgeArea: item.knowledgeArea || '-',
    classType: item.classType || '-',
    classDegree: item.classDegree || '-',
    modality: item.modality || '-',
    campus: item.campus || '-',
    location: item.location || '-',
    shift: item.shift || '-',
    startDate: formatDate(item.startDate),
    endDate: formatDate(item.endDate),
    academicPeriod: item.academicPeriod || '-',
    yearValidity: item.yearValidity || '-',
    createdAt: formatDate(item.createdAt)
  })) : [];

  const handlePageClick = (page) => {
    setCurrentPage(page);
    fetchConsolidatedData(page, searchTerm);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
    fetchConsolidatedData(1, term);
  };

  const getVisiblePages = () => {
    if (!paginationInfo) return [];
    
    const totalPages = paginationInfo.totalPages;
    const current = currentPage;
    let visiblePages = [];
    
    if (totalPages <= 7) {
      visiblePages = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      if (current <= 4) {
        visiblePages = [1, 2, 3, 4, 5, '...', totalPages];
      } else if (current >= totalPages - 3) {
        visiblePages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        visiblePages = [1, '...', current - 1, current, current + 1, '...', totalPages];
      }
    }
    
    return visiblePages;
  };

  const handleExportCsv = async () => {
    if (!institution?.id) {
      toast.error("Instituição não encontrada");
      return;
    }

    if (!lastApiResponse?.executedQuery) {
      toast.error("Nenhuma consulta disponível para exportação");
      return;
    }

    try {
      setExporting(true);
      toast.info("Preparando exportação...");

      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear - 3}-01-01`;
      const endDate = `${currentYear + 3}-12-31`;

      const exportQuery = lastApiResponse.executedQuery
        .replace(/OFFSET @Offset ROWS\s*FETCH NEXT @PageSize ROWS ONLY/gi, '');

      const exportPayload = {
        executedQuery: exportQuery,
        parameters: {
          InstitutionId: institution.id,
          StartDate: startDate,
          EndDate: endDate,
        },
        headers: {
          ClassId: "ID da Turma",
          ClassName: "Nome da Turma",
          ClassStatus: "Status",
          KnowledgeArea: "Área de Conhecimento",
          ClassType: "Tipo da Turma",
          ClassDegree: "Grau",
          Modality: "Modalidade",
          Campus: "Campus",
          Location: "Localização",
          Shift: "Turno",
          StartDate: "Data de Início",
          EndDate: "Data de Término",
          AcademicPeriod: "Período Acadêmico",
          YearValidity: "Ano de Validade",
          CreatedAt: "Data de Importação"
        },
        fileName: "dados_turmas_consolidados.csv",
        includeHeaders: true,
        delimiter: ";",
        culture: "pt-BR"
      };

      if (searchTerm) {
        exportPayload.parameters.SearchTerm = searchTerm;
      }

      const response = await api.client.post(
        'api/v1/CsvExport/export-from-query',
        exportPayload,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            institutionId: institution.id
          }
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportPayload.fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");

    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (institution?.id) {
      fetchConsolidatedData(1, "");
    }
  }, [institution?.id, fetchConsolidatedData]);

  return (
    <div className={styles.consolidatedDataContent}>
      {/* Header */}
      <HeaderTitle title="Dados Consolidados de Turmas" icon={DatabaseIcon} />

      <div className={styles.consolidatedDataBody}>
        <div className={styles.tableContainer}>
          {/* Barra de pesquisa e exportação */}
          <div className={styles.tableActions}>
            <div className={styles.searchInputWrapper}>
              <img src={SearchIcon} alt="Pesquisar" className={styles.searchIconTable} />
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInputTable}
              />
            </div>
            <button
              className={styles.exportCsvButton}
              onClick={handleExportCsv}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <img src={loadingGif} alt="Exportando..." className={styles.exportIconTable} />
                  Exportando...
                </>
              ) : (
                <>
                  <img src={FileExportIcon} alt="Exportar" className={styles.exportIconTable} />
                  Exportar CSV
                </>
              )}
            </button>
          </div>
        
          <div className={styles.tableWrapper}>
          <table className={styles.consolidatedDataTable}>
              <thead>
                <tr>
                  <th>ID da Turma</th>
                  <th>Nome da Turma</th>
                  <th style={{ textAlign: 'center' }}>Status da Turma</th>
                  <th>Área de Conhecimento</th>
                  <th>Tipo do Curso</th>
                  <th>Grau</th>
                  <th>Modalidade</th>
                  <th>Campus</th>
                  <th>Localização</th>
                  <th>Turno</th>
                  <th>Data de Início</th>
                  <th>Data de Término</th>
                  <th>Período Acadêmico</th>
                  <th>Ano de Validade</th>
                  <th>Data de Importação</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="15" style={{ textAlign: 'center', padding: '40px' }}>
                      <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                      <p>Carregando dados consolidados de turmas...</p>
                    </td>
                  </tr>
                ) : processedConsolidatedData.length > 0 ? (
                  processedConsolidatedData.map((item, index) => (
                    <tr key={`${item.classId}-${index}`}>
                      <td>{item.classId}</td>
                      <td>{item.className}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`${styles.statusBadge} ${styles[`status${item.classStatus}`]}`}>
                          {item.classStatus}
                        </span>
                      </td>
                      <td>{item.knowledgeArea}</td>
                      <td>{item.classType}</td>
                      <td>{item.classDegree}</td>
                      <td>{item.modality}</td>
                      <td>{item.campus}</td>
                      <td>{item.location}</td>
                      <td>{item.shift}</td>
                      <td>{item.startDate}</td>
                      <td>{item.endDate}</td>
                      <td>{item.academicPeriod}</td>
                      <td>{item.yearValidity}</td>
                      <td>{item.createdAt}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="15" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                      Nenhum dado consolidado de turma encontrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </div>
        
        <div className={styles.pagination}>
          {paginationInfo && paginationInfo.totalPages > 1 && (
            <>
              <button
                className={styles.paginationArrow}
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
                title="Página anterior"
              >
                ←
              </button>
              
              {getVisiblePages().map((page, index) => (
                <button
                  key={`page-${page}-${index}`}
                  className={
                    page === '...' 
                      ? styles.paginationDots 
                      : page === currentPage 
                        ? styles.activePage 
                        : styles.paginationButton
                  }
                  onClick={() => page !== '...' && handlePageClick(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}
              
              <button
                className={styles.paginationArrow}
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === paginationInfo.totalPages}
                title="Próxima página"
              >
                →
              </button>
            </>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default ClassConsolidatedData;


